import SideBar from "@components/sidebar/sidebar";
import classNames from "@lib/classNames";
import Image from "next/image";
import Link from "next/link";
import StickyBox from "react-sticky-box";
import Layout from "@components/layout/layout";
import { NewsPublisherDetailsFragment } from "@graphql/generated/graphql";
import { PagePublishersLandingComp } from "@graphql/generated/page";
import { Tab } from "@headlessui/react";
import { useState } from "react";
import { useRouter } from "next/router";
import { Trans } from "@lingui/macro";

interface IPublisherListIconProps {
  publisher: NewsPublisherDetailsFragment;
}

const PublishersLandingPage: PagePublishersLandingComp = ({ data }) => {
  const router = useRouter();
  const { locale, pathname, query } = router;
  const LANGUAGE_CODE = ["en", "ms", "zh"];

  let [publishers] = useState({
    English: {
      newsPublishers: data.languages.find((language) => language.code === "en")
        .newsPublishers,
    },

    "Bahasa Melayu": {
      newsPublishers: data.languages.find((language) => language.code === "ms")
        .newsPublishers,
    },
    中文: {
      newsPublishers: data.languages.find((language) => language.code === "zh")
        .newsPublishers,
    },
  });

  return (
    <Layout showNewsStacks={true} className="!overflow-visible">
      <div className="mx-auto my-6 flex max-w-7xl flex-row items-start px-4 2xs:px-6 lg:space-x-20">
        <div className="w-full flex-1 space-y-8">
          <Tab.Group
            key={query.language as string}
            defaultIndex={LANGUAGE_CODE.findIndex(
              (code) => code === (query.language || locale)
            )}
            onChange={(index) => {
              let query = { language: LANGUAGE_CODE[index] };
              router.push({ pathname, query }, undefined, {
                shallow: true,
              });
            }}
          >
            <Tab.List className="no-scrollbar my-8 flex flex-nowrap overflow-x-scroll">
              {Object.keys(publishers).map((language) => (
                <Tab
                  key={language}
                  className={({ selected }) =>
                    classNames(
                      `rounded-2xl ${
                        selected
                          ? "bg-primary text-white"
                          : "bg-secondary text-primary"
                      }  mr-2 cursor-pointer whitespace-nowrap px-4 py-2 font-sen`
                    )
                  }
                >
                  {language}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels>
              {Object.values(publishers).map((publisherList, idx) => (
                <Tab.Panel
                  key={idx}
                  className="my-8 flex-col md:grid md:grid-cols-3 md:gap-y-12 md:gap-x-24"
                >
                  {publisherList.newsPublishers.map((publisher) => (
                    <div key={publisher["id"]}>
                      <PublisherListIcon publisher={publisher} />
                    </div>
                  ))}
                </Tab.Panel>
              ))}
              <div className="rounded-lg bg-secondary px-4 py-3">
                <p className="mb-4 font-sen font-bold text-text-darkest">
                  <Trans>
                    🔎 We're looking for publishers that create quality content
                    with fact-checking measures.
                  </Trans>
                </p>
                <p className="font-sen text-text-darkest">
                  <Trans>Think you fit the bill? Fill up this</Trans>{" "}
                  <Link href="/publisher-application">
                    <a
                      rel="noreferrer"
                      href="https://docs.google.com/forms/d/e/1FAIpQLSfMtBnhzPnGHtNwzP68Klb7N5al6pI854SPzD-v75wliK-jiQ/viewform?pli=1"
                      target="_blank"
                      className="text-primary2"
                    >
                      <Trans>form</Trans>
                    </a>
                  </Link>{" "}
                  <Trans>and we'll get back to you.</Trans>
                </p>
              </div>
            </Tab.Panels>
          </Tab.Group>
        </div>
        <StickyBox offsetTop={20} offsetBottom={20}>
          <SideBar />
        </StickyBox>
      </div>
    </Layout>
  );
};

const PublisherListIcon: React.FunctionComponent<IPublisherListIconProps> = ({
  publisher,
}) => {
  return (
    <div>
      <div className="hidden md:block">
        <WidePublisherListIcon publisher={publisher} />
      </div>
      <div className="block md:hidden">
        <CompactPublisherListIcon publisher={publisher} />
      </div>
    </div>
  );
};

const WidePublisherListIcon: React.FunctionComponent<
  IPublisherListIconProps
> = ({ publisher: { imageUrl, name, slug, description } }) => {
  return (
    <Link href={`/publishers/${slug}`}>
      <a>
        <div className="flex flex-col">
          <div
            style={{
              filter: "drop-shadow(1px 1px 8px rgba(147, 154, 188, 0.4)",
            }}
          >
            <Image
              className="rounded-lg"
              src={imageUrl}
              alt={name}
              width={161}
              height={101}
              objectFit="cover"
              priority
            />
          </div>
          <p className="mt-4 font-poppins font-bold text-text-darkest">
            {name}
          </p>
          <p className="mt-2 font-sen text-text-medium">{description}</p>
        </div>
      </a>
    </Link>
  );
};

const CompactPublisherListIcon: React.FunctionComponent<
  IPublisherListIconProps
> = ({ publisher: { imageUrl, name, slug, description } }) => {
  return (
    <div className="mb-8 divide-y divide-light-background">
      <Link href={`/publishers/${slug}`}>
        <a>
          <div className="flex flex-col">
            <div className="flex flex-row items-center space-x-4">
              <div
                className="relative aspect-[1.6] w-[76px]"
                style={{
                  filter: "drop-shadow(1px 1px 8px rgba(147, 154, 188, 0.4)",
                }}
              >
                <Image
                  className="rounded-lg"
                  src={imageUrl}
                  alt={name}
                  width={80}
                  height={48}
                  objectFit="cover"
                  priority
                />
              </div>
              <p className="font-poppins font-bold text-text-darkest">{name}</p>
            </div>
            <p className="mt-2 font-sen text-text-medium">{description}</p>
          </div>
        </a>
      </Link>
      <div className="mt-8"></div>
    </div>
  );
};

export default PublishersLandingPage;
